import React from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import {
    AsyncTypeahead,
    InputContainerPropsSingle,
    Menu,
    MenuItem,
    MenuProps,
    Typeahead,
    TypeaheadMenuProps,
} from 'react-bootstrap-typeahead';
import OpenBookIcon from '../../svgs/openbook.svg';
import { LinkModel } from '../../models/globals';
import { xhrClient } from '../../services/xhrClient';
import SearchIcon from '../../svgs/search.svg';
import { SearchParamNames } from '../../models/enums';
import { RenderContext } from '../../utilities/RenderContext';
import { updateClassNamesWithPrefix } from '../../utilities/CssUtilities';
import { removeExtraWhitespace } from '../../utilities/StringUtilities';

const seeAllCoursesName = 'See All Courses';

interface SearchCoursesProps {
    autocompleteApiPath: string;
    className?: string;
    id: string;
    inputValue?: string;
    isLoadingResults?: boolean;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    placeholder?: string;
    searchPath: string;
    useHomePageSettings?: boolean;
}

interface SearchButtonProps {
    disabled?: boolean;
    ref: React.RefObject<HTMLInputElement>;
    size?: 'lg' | 'sm';
    type: string;
    value: string;
    as?: 'input';
}

const SearchCourses: React.FC<SearchCoursesProps> = ({
    autocompleteApiPath,
    className,
    id,
    inputValue,
    isLoadingResults,
    onSubmit,
    placeholder = 'Search courses',
    searchPath,
    useHomePageSettings = false,
}) => {
    const seeAllCourses: LinkModel = {
        url: searchPath,
        name: seeAllCoursesName,
    };
    const {
        form,
        handleKeyDown,
        isLoading,
        options,
        setAutocompleteSearchTerms,
        submitButton,
        typeAhead,
    } = useAutocomplete({ autocompleteApiPath, inputValue, seeAllCourses });
    const searchButtonProps: SearchButtonProps = {
        disabled: isLoadingResults,
        ref: submitButton,
        size: 'lg',
        type: 'submit',
        value: isLoadingResults ? 'Loading...' : 'Search',
        as: 'input',
    };
    const omitSearchParamIfEmpty: React.FormEventHandler<HTMLFormElement> = () => {
        /**
         * To avoid extra browser history entries on the search page and for a cleaner URL,
         * omit the search param ("term=") when submitting the form with an empty search input.
         * Deleting the formData from the form would be better, but we'd need a polyfill for IE.
         */
        const searchInput = typeAhead.current?.getInput();
        if (searchInput && removeExtraWhitespace(searchInput?.value) === '') {
            searchInput.name = '';
        }
    };

    return (
        <RenderContext.Consumer>
            {(value) => (
                <Form
                    action={searchPath}
                    className={className}
                    id={`${id}-form`}
                    onSubmit={(e) => {
                        typeAhead.current?.setState({
                            text: removeExtraWhitespace(typeAhead.current?.getInput().value),
                        });

                        const handler = onSubmit || omitSearchParamIfEmpty;
                        handler(e);
                    }}
                    ref={form}
                    data-testid="search-courses-form"
                >
                    <InputGroup data-testid="searchbox-input-group">
                        {useHomePageSettings && (
                            <InputGroup.Prepend>
                                <InputGroup.Text className="bg-white border-0">
                                    <SearchIcon className="home-search-icon" />
                                </InputGroup.Text>
                            </InputGroup.Prepend>
                        )}
                        <AsyncTypeahead
                            className={updateClassNamesWithPrefix(
                                `${useHomePageSettings ? 'searchbox-home-border search-bg-clip' : ''}`,
                                value.mode,
                            )}
                            defaultInputValue={inputValue}
                            filterBy={() => true}
                            id={id}
                            isLoading={isLoading}
                            labelKey="name"
                            onChange={([selected]: LinkModel[]) => {
                                window.open(selected.url, '_self');
                            }}
                            onKeyDown={handleKeyDown}
                            onSearch={(terms) => setAutocompleteSearchTerms(removeExtraWhitespace(terms))}
                            options={options}
                            placeholder={placeholder}
                            ref={typeAhead}
                            // Typeahead's renderInput type def is incorrect
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            renderInput={(props: any) => (
                                <SearchInput
                                    {...props}
                                    className={updateClassNamesWithPrefix(
                                        useHomePageSettings ? 'border-0' : '',
                                        value.mode,
                                    )}
                                />
                            )}
                            renderMenu={(results, menuProps) => (
                                <SearchMenu menuProps={menuProps} results={results} typeAhead={typeAhead} />
                            )}
                            size="lg"
                            useCache={false}
                        />
                        <InputGroup.Append
                            className={updateClassNamesWithPrefix(
                                useHomePageSettings ? 'd-none d-md-block' : '',
                                value.mode,
                            )}
                        >
                            <Button {...searchButtonProps} data-testid="search-button" />
                        </InputGroup.Append>
                    </InputGroup>
                    {useHomePageSettings && (
                        <Button
                            {...searchButtonProps}
                            className="my-2 w-100 d-md-none"
                            data-testid="home-mobile-search-button"
                        />
                    )}
                </Form>
            )}
        </RenderContext.Consumer>
    );
};

const useAutocomplete = ({
    autocompleteApiPath,
    inputValue = '',
    seeAllCourses,
}: {
    autocompleteApiPath: string;
    inputValue?: string;
    seeAllCourses: LinkModel;
}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [options, setOptions] = React.useState<LinkModel[]>([]);
    // autocompleteSearchTerms are not necessarily in sync with parent component searchTerms
    const [autocompleteSearchTerms, setAutocompleteSearchTerms] = React.useState(inputValue);
    const typeAhead = React.useRef<AsyncTypeahead<LinkModel> & Typeahead<LinkModel>>(null);
    const submitButton = React.useRef<HTMLInputElement>(null);
    const form = React.useRef<HTMLFormElement>(null);

    const handleKeyDown = (e: Event) => {
        switch ((e as KeyboardEvent).key) {
            case 'Enter': {
                if (typeAhead.current?.getInput().hasAttribute('aria-activedescendant')) {
                    break; // onChange will fire, don't load unnecessary data before navigating
                }
                e.preventDefault();
                // Simulate clicking the submit button to fire form.onSubmit if it exists.
                // form.current?.submit() does not trigger onSubmit, and
                // form.current?.requestSubmit() is not available in IE
                submitButton.current?.click();
                break;
            }
            case 'Escape': {
                typeAhead.current?.clear();
                break;
            }
        }
    };

    React.useEffect(() => {
        if (autocompleteSearchTerms.length < 2) {
            return setIsLoading(false); // just in case a previous request was canceled
        }

        const { cancel, isCancel, promise } = xhrClient.getAutocompleteSearchResult(
            autocompleteApiPath,
            autocompleteSearchTerms,
        );
        setOptions([]);
        setIsLoading(true);

        (async function getOptions() {
            try {
                const { data } = await promise;
                if (data.productLinks.length) {
                    setOptions([...data.productLinks, seeAllCourses]);
                }
                setIsLoading(false);
            } catch (thrown) {
                if (!isCancel(thrown)) {
                    console.error(thrown);
                    setIsLoading(false);
                }
            }
        })();

        return function cleanup() {
            cancel(`Canceled autocomplete search for "${autocompleteSearchTerms}"`);
        };
    }, [autocompleteSearchTerms]);

    React.useEffect(() => {
        setAutocompleteSearchTerms(inputValue);
        typeAhead.current?.setState({ text: inputValue });
    }, [inputValue]);

    return {
        form,
        handleKeyDown,
        isLoading,
        options,
        seeAllCourses,
        setAutocompleteSearchTerms,
        submitButton,
        typeAhead,
    };
};

interface SearchInputProps extends Omit<InputContainerPropsSingle<LinkModel>, 'onChange'> {
    className?: string;
    inputRef(instance: HTMLInputElement | null): void;
    referenceElementRef(instance: HTMLInputElement | null): void;
}

const SearchInput: React.FC<SearchInputProps> = ({ className, inputRef, referenceElementRef, ...inputProps }) => {
    // Don't wrap with Hint, it botches keyboard-tabbing and input::selection styles and fails WAVE.
    return (
        <Form.Control
            {...inputProps}
            aria-label="Search courses"
            autoComplete="off"
            className={className}
            name={SearchParamNames.Query}
            ref={(element: HTMLInputElement | null) => {
                inputRef(element);
                referenceElementRef(element);
            }}
            size="lg"
            type="search"
        />
    );
};

interface SearchMenuProps {
    menuProps:
        | TypeaheadMenuProps<LinkModel>
        | (JSX.IntrinsicAttributes &
              JSX.IntrinsicClassAttributes<Menu> &
              Readonly<MenuProps> &
              Readonly<{ children?: React.ReactNode }>);
    results: LinkModel[];
    typeAhead: React.RefObject<AsyncTypeahead<LinkModel> & Typeahead<LinkModel>>;
}

const SearchMenu: React.FC<SearchMenuProps> = ({ results, menuProps, typeAhead }) => {
    const currentInput = removeExtraWhitespace(typeAhead.current?.getInput().value);

    if (!currentInput) return null;

    return (
        <RenderContext.Consumer>
            {(value) => (
                <Menu {...menuProps} className="m-0 p-0">
                    {results.map((result, index) => (
                        <MenuItem
                            className="dropdown-item border-bottom"
                            data-testid={`data-${result.name}`}
                            href={result.url}
                            key={`${result.name}_${index}`}
                            option={{ ...result, name: currentInput }}
                            position={index}
                        >
                            <OpenBookIcon
                                className={updateClassNamesWithPrefix(
                                    `mr-2 text-secondary ${
                                        result.name === seeAllCoursesName ? 'invisible' : ''
                                    }`.trim(),
                                    value.mode,
                                )}
                            />
                            {result.name}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </RenderContext.Consumer>
    );
};

export { SearchCourses, SearchCoursesProps };
